<template>
  <manual-page
    title="Primeros Pasos"
    description="Guía completa para comenzar a usar el panel de franquicia Trunimal y conocer todas sus funciones."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'ordersIndex' }"
    action-text="Ir a Órdenes"
  >
    <!-- Welcome -->
    <h2>Bienvenido a Trunimal Franquicias</h2>
    <p>
      El panel de franquicia es tu centro de operaciones para gestionar la producción
      y entrega de alimentos naturales para mascotas. Desde aquí puedes procesar órdenes,
      planificar producción, controlar inventario y más.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Funciones principales disponibles:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Órdenes:</strong> Procesar, preparar y entregar pedidos de clientes</li>
        <li><strong>Programa de Producción:</strong> Planificar la elaboración de alimentos</li>
        <li><strong>Clientes:</strong> Consultar información de clientes y sus mascotas</li>
        <li><strong>Mascotas:</strong> Ver perfiles y dietas de las mascotas</li>
        <li><strong>Inventario:</strong> Controlar el stock de ingredientes</li>
        <li><strong>Usuarios:</strong> Administrar accesos del personal</li>
        <li><strong>Contabilidad:</strong> Registrar ingresos y egresos</li>
        <li><strong>Reportes:</strong> Consultar estadísticas de operación</li>
      </ul>
    </div>

    <!-- Dashboard -->
    <h2>Panel Principal (Dashboard)</h2>
    <p>
      Al iniciar sesión, llegas al panel principal que muestra un resumen
      de la situación actual de tu sucursal.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Resumen de Órdenes</h3>
          <p class="text-gray-600">
            Vista rápida de órdenes pendientes, en preparación y listas para entrega.
            Haz clic para ir directamente a cada sección.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Producción del Día</h3>
          <p class="text-gray-600">
            Número de órdenes programadas para hoy y los próximos días.
            Te ayuda a planificar la carga de trabajo.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Alertas y Notificaciones</h3>
          <p class="text-gray-600">
            Avisos importantes como solicitudes de adelanto, problemas de pago
            o inventario bajo.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Revisa el dashboard cada mañana para conocer
        el estado de tu operación y priorizar las tareas del día.
      </p>
    </div>

    <!-- Navigation -->
    <h2>Navegación del Sistema</h2>
    <p>
      El menú lateral izquierdo te da acceso a todas las secciones del sistema.
      La barra superior contiene accesos rápidos y notificaciones.
    </p>

    <h3>Menú Principal</h3>
    <ul>
      <li><strong>Órdenes:</strong> Gestión completa de pedidos</li>
      <li><strong>Programa de Producción:</strong> Planificación diaria</li>
      <li><strong>Clientes:</strong> Información de clientes</li>
      <li><strong>Mascotas:</strong> Perfiles de mascotas</li>
      <li><strong>Inventario:</strong> Control de stock</li>
      <li><strong>Usuarios:</strong> Gestión de personal</li>
      <li><strong>Contabilidad:</strong> Ingresos y egresos</li>
      <li><strong>Reportes:</strong> Estadísticas y análisis</li>
      <li><strong>Manual:</strong> Esta guía de ayuda</li>
    </ul>

    <h3>Barra Superior</h3>
    <ul>
      <li><strong>Notificaciones:</strong> Icono de campana con contador de pendientes</li>
      <li><strong>Perfil:</strong> Acceso a tu cuenta y configuración</li>
      <li><strong>Selector de Sucursal:</strong> Si administras múltiples sucursales</li>
    </ul>

    <!-- Multi-Branch -->
    <h2>Gestión de Múltiples Sucursales</h2>
    <p>
      Si tu franquicia tiene varias sucursales, puedes administrarlas
      todas desde el mismo panel.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Selector de Sucursal</h3>
          <p class="text-gray-600">
            En el menú lateral encontrarás un selector que muestra tu sucursal actual.
            Haz clic para cambiar entre sucursales.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Datos Independientes</h3>
          <p class="text-gray-600">
            Cada sucursal tiene sus propios clientes, órdenes, inventario y contabilidad.
            Al cambiar de sucursal, todos los datos se actualizan.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Reportes Consolidados</h3>
          <p class="text-gray-600">
            Algunos reportes permiten ver datos de todas las sucursales juntas
            o comparar el rendimiento entre ellas.
          </p>
        </div>
      </div>
    </div>

    <div class="info-box">
      <p class="text-sm text-gray-700">
        <strong>Nota:</strong> El acceso a sucursales depende de los permisos de tu usuario.
        Si necesitas acceso a una sucursal adicional, contacta al administrador.
      </p>
    </div>

    <!-- Notifications -->
    <h2>Sistema de Notificaciones</h2>
    <p>
      El sistema te alerta sobre eventos importantes que requieren tu atención.
      Las notificaciones aparecen en el icono de campana de la barra superior.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Tipos de notificaciones:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Nuevas órdenes pagadas:</strong> Pedidos listos para producción</li>
        <li><strong>Solicitudes de adelanto:</strong> Clientes que piden cambiar fecha de entrega</li>
        <li><strong>Pagos fallidos:</strong> Problemas con cobros de clientes</li>
        <li><strong>Cambios en planes:</strong> Pausas, cancelaciones o reactivaciones</li>
        <li><strong>Stock bajo:</strong> Ingredientes con inventario bajo</li>
      </ul>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> El contador en el icono de campana muestra las notificaciones
        sin leer. Revísalas al menos dos veces al día para no perder información importante.
      </p>
    </div>

    <!-- Order Flow -->
    <h2>Flujo de Órdenes</h2>
    <p>
      Entender el ciclo de vida de las órdenes es fundamental para operar correctamente.
      Cada orden pasa por los siguientes estados:
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Pendiente de Pago</h3>
          <p class="text-gray-600">
            La orden se generó automáticamente según el plan del cliente.
            El sistema intentará cobrar en la fecha programada.
            No requiere acción de tu parte.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Pagada</h3>
          <p class="text-gray-600">
            El cobro fue exitoso. La orden entra a tu cola de producción.
            Revisa la fecha de entrega para priorizar.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">En Preparación</h3>
          <p class="text-gray-600">
            Estás elaborando el alimento. Marca este estado cuando
            inicies la producción para llevar control.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Lista para Envío / Recolección</h3>
          <p class="text-gray-600">
            El alimento está preparado y empacado. Dependiendo del tipo:
            programa la entrega a domicilio o espera a que el cliente
            pase a recoger.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">5</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Entregada</h3>
          <p class="text-gray-600">
            Orden completada exitosamente. El cliente recibió su alimento.
            Marca este estado para cerrar la orden.
          </p>
        </div>
      </div>
    </div>

    <!-- Daily Operations -->
    <h2>Operación Diaria Típica</h2>
    <p>
      Una rutina organizada ayuda a mantener la operación eficiente.
      Te sugerimos seguir estos pasos cada día:
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Inicio del Día</h3>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Revisa el dashboard para ver el panorama general</li>
            <li>Consulta las notificaciones pendientes</li>
            <li>Verifica las órdenes programadas para hoy</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Producción</h3>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Usa el Programa de Producción para planificar</li>
            <li>Verifica que tengas los ingredientes necesarios</li>
            <li>Procesa las órdenes en lotes por tipo de alimento</li>
            <li>Actualiza el estado de las órdenes en tiempo real</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Entregas</h3>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Coordina los envíos a domicilio</li>
            <li>Atiende a clientes que recojan en sucursal</li>
            <li>Marca las órdenes como entregadas al completar</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Cierre del Día</h3>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Registra gastos del día en contabilidad</li>
            <li>Actualiza el inventario si hubo movimientos</li>
            <li>Revisa las órdenes de mañana para planificar</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Permissions -->
    <h2>Sistema de Permisos</h2>
    <p>
      El acceso a las diferentes secciones del sistema está controlado
      por roles y permisos. Cada usuario tiene asignado un rol que define
      qué puede ver y hacer.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Roles típicos:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Administrador:</strong> Acceso total a todas las funciones</li>
        <li><strong>Operador:</strong> Gestión de órdenes y producción</li>
        <li><strong>Contador:</strong> Acceso a contabilidad y reportes</li>
      </ul>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Si no puedes ver alguna sección o realizar alguna acción,
        es probable que tu rol no tenga ese permiso. Contacta al administrador
        de tu franquicia para solicitar acceso.
      </p>
    </div>

    <!-- Profile Settings -->
    <h2>Configuración de Perfil</h2>
    <p>
      Desde tu perfil puedes actualizar tu información personal
      y cambiar tu contraseña.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al Perfil</h3>
          <p class="text-gray-600">
            Haz clic en tu nombre o foto en la esquina superior derecha
            y selecciona "Mi perfil".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Cambiar Contraseña</h3>
          <p class="text-gray-600">
            Por seguridad, te recomendamos cambiar tu contraseña regularmente.
            Usa una contraseña fuerte con mayúsculas, números y símbolos.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Cerrar Sesión</h3>
          <p class="text-gray-600">
            Siempre cierra sesión cuando termines de usar el sistema,
            especialmente en computadoras compartidas.
          </p>
        </div>
      </div>
    </div>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>
      Si tienes dudas sobre el uso del sistema, puedes contactar al equipo de soporte:
    </p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Explora las demás secciones del manual para conocer
        a detalle cada función del sistema.
      </p>
    </div>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualGettingStarted",
  components: { ManualPage },
  data() {
    return {
      relatedLinks: [
        { route: "manualOrders", label: "Órdenes" },
        { route: "manualProductionSchedule", label: "Programa de Producción" },
        { route: "manualClients", label: "Clientes" },
        { route: "manualInventory", label: "Inventario" },
      ],
    };
  },
};
</script>
